import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, ButtonMode } from '@components/atoms/Button';
import { Table } from '@components/atoms/Table';
import { formatPrice } from '@utils/format';
import { Typography, TypographyTagVariants } from '@components/atoms/Typography';
import { useMediaQuery } from '@hooks/useMediaQuery';
import { Checkbox, CheckboxType } from '@components/atoms/Checkbox';
import { useBookLockerCompartment } from '@services/useBookLockerCompartment';
import { ErrorMessages } from '@components/atoms/ErrorMessages';
import { Popup } from '@components/atoms/Popup';
import styles from './BookLockerCompartmentsModal.module.css';
import { useGetMyStoresOrderDetail } from '@services/useGetMyStoresOrderDetail';
import { Loader } from '@components/molecules/Pagination';
import { useData } from '@hooks/useData';
import { useNotifications } from '@hooks/useNotifications';

interface BookLockerCompartmentsModalProps {
    orderUuid: string;
    storeUuid: string;
    products: {
        orderItemUuid: string;
        name: string;
        price?: number;
        quantity?: number;
        attributes: string[];
    }[];
    show: boolean;
    onClose: () => void;
}

export const BookLockerCompartmentsModal: FC<BookLockerCompartmentsModalProps> = ({
    orderUuid,
    storeUuid,
    products,
    show,
    onClose,
}) => {
    const pageSize = useMediaQuery();
    const { showDefaultNotification } = useNotifications();
    const { isLoadingMyStoresOrderDetail } = useData();
    const { request: requestMyStoresOrderDetail } = useGetMyStoresOrderDetail();
    const {
        request: requestBookLockerCompartment,
        loading,
        success,
        errorMessage,
        validationErrors,
        lastResponseTime,
    } = useBookLockerCompartment();
    const { t } = useTranslation();

    const translations = {
        title: t('book-locker-compartments-modal-component.title'),
        bookButton: t('book-locker-compartments-modal-component.book.button'),
        name: t('book-locker-compartments-modal-component.table.name'),
        attributes: t('book-locker-compartments-modal-component.table.attributes'),
        quantity: t('book-locker-compartments-modal-component.table.quantity'),
        price: t('book-locker-compartments-modal-component.table.price'),
    };

    const [checkedOrderItems, setCheckedOrderItems] = useState<string[]>([]);
    const [showError, setShowError] = useState<boolean>(false);

    useEffect(() => {
        if (lastResponseTime) {
            // show error when it's a new endpoint call with error
            if (errorMessage || validationErrors) {
                setShowError(true);
            } else {
                showDefaultNotification({ type: 'success', message: success || '' });
                // update page data
                requestMyStoresOrderDetail({ order_uuid: orderUuid, store_uuid: storeUuid });
            }
        }
    }, [lastResponseTime]);

    // hide error when user checks/unchecks items
    useEffect(() => {
        setShowError(false);
    }, [checkedOrderItems]);

    useEffect(() => {
        if (loading) {
            setShowError(false);
        }
    }, [loading]);

    useEffect(() => {
        setCheckedOrderItems(products.map((product) => product.orderItemUuid || '') || []);

        // close modal if there are no more items to book to locker
        if (show && products.length === 0) {
            onClose();
        }
    }, [products]);

    const handleOnClickBook = () => {
        requestBookLockerCompartment({ order_uuid: orderUuid, store_uuid: storeUuid, order_items_uuids: checkedOrderItems });
    };

    const header = [
        { text: '' },
        { text: translations.name },
        { text: translations.attributes },
        { classes: 'text-start text-md-center', text: translations.quantity },
        { classes: 'text-end', hideOnMobile: true, text: translations.price },
    ];

    const content = products.map((product) => {
        return {
            cells: [
                {
                    content: (
                        <Checkbox
                            type={CheckboxType.normal}
                            name="to-book"
                            value={product.orderItemUuid}
                            checked={
                                checkedOrderItems.find((checkedOrderItem) => product.orderItemUuid === checkedOrderItem)
                                    ? true
                                    : false
                            }
                            onChange={() =>
                                setCheckedOrderItems(
                                    (prev) =>
                                        prev.includes(product.orderItemUuid || '')
                                            ? prev.filter((id) => id !== product.orderItemUuid) // remove if already checked
                                            : [...prev, product.orderItemUuid || ''] // add if not checked
                                )
                            }
                            label=""
                            disabled={loading}
                        />
                    ),
                },
                { content: product.name },
                { content: product.attributes?.join(', ') || '' },
                { classes: 'text-start text-md-center', content: `${product.quantity}` },
                { classes: 'text-end', content: formatPrice(product.price) },
            ],
            options: [],
        };
    });

    const contentCommonClasses = ['xs', 'sm', 'md'].includes(pageSize) ? 'text-truncate' : '';

    return (
        <Popup
            show={show}
            onHide={onClose}
            desktopWidth="1220px"
            fullWidthOnMobile={true}
            header={
                <>
                    <div className={styles.header}>
                        <Typography tagVariant={TypographyTagVariants.h5} fontWeight="900" fontSize="20">
                            {translations.title}
                        </Typography>
                    </div>
                    <hr className={styles.divider} />
                </>
            }>
            <div className={styles.content}>
                <div className="d-flex flex-column gap-3">
                    {!isLoadingMyStoresOrderDetail ? (
                        <Table
                            compressedSize={true}
                            headerCols={header}
                            contentRows={content.map((row) => {
                                return row.cells.map((cell) => {
                                    return {
                                        classes: cell.classes,
                                        content: (
                                            <div className="d-grid">
                                                <Typography
                                                    classes={contentCommonClasses}
                                                    fontSize="16"
                                                    tagVariant={TypographyTagVariants.span}>
                                                    {cell.content}
                                                </Typography>
                                            </div>
                                        ),
                                    };
                                });
                            })}
                        />
                    ) : (
                        <Loader />
                    )}

                    <Button
                        label={translations.bookButton}
                        classes="w-100"
                        mode={ButtonMode.primary}
                        onClick={handleOnClickBook}
                        disabled={loading || isLoadingMyStoresOrderDetail}
                    />

                    <>
                        {showError && (
                            <>
                                {errorMessage && showError && <ErrorMessages messages={[errorMessage]} />}
                                {validationErrors && showError && (
                                    <ErrorMessages messages={(Object.values(validationErrors).flat() as string[]) || []} />
                                )}
                            </>
                        )}
                    </>
                </div>
            </div>
        </Popup>
    );
};
